import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { readCommunityTour, saveCommunityTour } from '@/lib/communityTourStorage';
import { COMMUNITY_TOUR_VERSION, pendingCommunityTour, shouldOfferCommunityTour, type CommunityTourState } from '@/lib/communityTourState';

/** Completion is stored on the account so the tour never repeats across sign-ins or devices. */
async function markTourDoneOnAccount(userId: string) {
  await supabase.from('profiles')
    .update({ community_tour_completed_at: new Date().toISOString() } as never)
    .eq('user_id', userId);
}

export function useCommunityTour(userId: string | undefined, onHome: boolean) {
  const [resolvedUser, setResolvedUser] = useState<string>();
  const [state, setState] = useState<CommunityTourState | null>(null);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    if (!userId || !onHome) return;
    const accountId = userId;
    let cancelled = false;
    async function load() {
      const saved = await readCommunityTour(accountId);
      const { data, error } = await supabase.from('profiles')
        .select('onboarding_completed, community_tour_completed_at').eq('user_id', accountId).maybeSingle();
      if (cancelled) return;
      const row = data as { onboarding_completed?: boolean; community_tour_completed_at?: string | null } | null;
      const doneOnAccount = Boolean(row?.community_tour_completed_at);
      const doneLocally = saved?.status === 'completed' || saved?.status === 'skipped';
      // Carry a device-only completion up to the account.
      if (!error && doneLocally && !doneOnAccount) void markTourDoneOnAccount(accountId);
      const effective: CommunityTourState | null = doneOnAccount
        ? { version: COMMUNITY_TOUR_VERSION, status: 'completed', step: 0 }
        : saved;
      const offer = !error && shouldOfferCommunityTour(row?.onboarding_completed === true, effective);
      const next = offer ? effective ?? pendingCommunityTour() : effective;
      setState(next);
      setEligible(offer);
      setResolvedUser(accountId);
      if (next && (offer || doneOnAccount)) void saveCommunityTour(accountId, next);
    }
    void load().catch(() => {
      if (!cancelled) { setEligible(false); setResolvedUser(accountId); }
    });
    return () => { cancelled = true; };
  }, [userId, onHome]);

  const ready = resolvedUser === userId;
  const pending = Boolean(userId && onHome && ready && eligible && state?.status === 'pending');
  const deferred = Boolean(userId && onHome && (!ready || pending));

  const update = (next: CommunityTourState) => {
    if (!userId) return;
    setState(next);
    void saveCommunityTour(userId, next);
    if (next.status !== 'pending') void markTourDoneOnAccount(userId);
  };

  return { pending, deferred, step: pending ? state?.step ?? 0 : 0, update };
}
